import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// SVG puzzle piece icon with traditional bulbous knob shape (narrow neck, rounded head)
function TraditionalPieceIcon({ className }) {
  // Path creates a piece with bulbous knobs that narrow at the neck
  // Top: outward bulbous tab, Right: inward bulbous slot
  const path = `
    M 6 6
    L 12 6
    Q 12 4 13 3
    Q 14.5 0 16 0
    Q 17.5 0 19 3
    Q 20 4 20 6
    L 26 6
    L 26 12
    Q 28 12 29 13
    Q 32 14.5 32 16
    Q 32 17.5 29 19
    Q 28 20 26 20
    L 26 26
    L 6 26
    Z
  `;

  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d={path} strokeLinejoin="round" />
    </svg>
  );
}

TraditionalPieceIcon.propTypes = {
  className: PropTypes.string,
};

TraditionalPieceIcon.defaultProps = {
  className: '',
};

// SVG puzzle piece icon with soft circular tabs (perfect semicircles)
function RoundedPieceIcon({ className }) {
  // Semicircular tabs - smooth, modern look
  const path = `
    M 6 6
    L 12 6
    A 4 4 0 0 0 20 6
    L 26 6
    L 26 12
    A 4 4 0 0 1 26 20
    L 26 26
    L 6 26
    Z
  `;

  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d={path} strokeLinejoin="round" />
    </svg>
  );
}

RoundedPieceIcon.propTypes = {
  className: PropTypes.string,
};

RoundedPieceIcon.defaultProps = {
  className: '',
};

// SVG puzzle piece icon with angular, blocky tabs
function SquarePieceIcon({ className }) {
  // Sharp 90-degree corners on tabs - geometric look
  const path = `
    M 6 6
    L 13 6
    L 13 2
    L 19 2
    L 19 6
    L 26 6
    L 26 13
    L 30 13
    L 30 19
    L 26 19
    L 26 26
    L 6 26
    Z
  `;

  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d={path} strokeLinejoin="round" />
    </svg>
  );
}

SquarePieceIcon.propTypes = {
  className: PropTypes.string,
};

SquarePieceIcon.defaultProps = {
  className: '',
};

// Grid preview component that overlays grid lines on the actual image area only
// (not the letterbox padding from object-contain)
function GridPreview({ imageData, rows, columns }) {
  const containerRef = useRef(null);
  const [imageBounds, setImageBounds] = useState(null);

  useEffect(() => {
    if (!imageData || !containerRef.current) return;

    const img = containerRef.current.querySelector('img');
    if (!img) return;

    const updateBounds = () => {
      const containerRect = containerRef.current.getBoundingClientRect();
      const imgNaturalWidth = img.naturalWidth;
      const imgNaturalHeight = img.naturalHeight;
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // Calculate rendered image size (same logic as object-contain)
      const imgAspect = imgNaturalWidth / imgNaturalHeight;
      const containerAspect = containerWidth / containerHeight;

      let renderedWidth;
      let renderedHeight;
      let offsetX;
      let offsetY;

      if (imgAspect > containerAspect) {
        // Image is wider - letterbox top/bottom
        renderedWidth = containerWidth;
        renderedHeight = containerWidth / imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - renderedHeight) / 2;
      } else {
        // Image is taller - letterbox left/right
        renderedHeight = containerHeight;
        renderedWidth = containerHeight * imgAspect;
        offsetX = (containerWidth - renderedWidth) / 2;
        offsetY = 0;
      }

      setImageBounds({
        width: renderedWidth, height: renderedHeight, x: offsetX, y: offsetY,
      });
    };

    if (img.complete) {
      updateBounds();
    } else {
      img.onload = updateBounds;
    }
  }, [imageData]);

  if (!imageData) return null;

  return (
    <div ref={containerRef} className="relative w-full">
      <img
        src={imageData}
        alt="Preview with grid"
        className="w-full max-h-64 object-contain rounded-lg border border-puzzle-border"
      />
      {imageBounds && (
        <svg
          className="absolute pointer-events-none top-0"
          style={{
            left: imageBounds.x,
            top: imageBounds.y,
            width: imageBounds.width,
            height: imageBounds.height,
          }}
        >
          {/* Vertical lines */}
          {Array.from({ length: columns - 1 }, (_, i) => (
            <line
              key={`v${i}`}
              x1={`${((i + 1) / columns) * 100}%`}
              y1="0"
              x2={`${((i + 1) / columns) * 100}%`}
              y2="100%"
              stroke="white"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          ))}
          {/* Horizontal lines */}
          {Array.from({ length: rows - 1 }, (_, i) => (
            <line
              key={`h${i}`}
              x1="0"
              y1={`${((i + 1) / rows) * 100}%`}
              x2="100%"
              y2={`${((i + 1) / rows) * 100}%`}
              stroke="white"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          ))}
          {/* Add dark outline for visibility on light images */}
          {Array.from({ length: columns - 1 }, (_, i) => (
            <line
              key={`vd${i}`}
              x1={`${((i + 1) / columns) * 100}%`}
              y1="0"
              x2={`${((i + 1) / columns) * 100}%`}
              y2="100%"
              stroke="rgba(0,0,0,0.3)"
              strokeWidth="1"
              strokeDasharray="5,5"
              strokeDashoffset="2.5"
            />
          ))}
          {Array.from({ length: rows - 1 }, (_, i) => (
            <line
              key={`hd${i}`}
              x1="0"
              y1={`${((i + 1) / rows) * 100}%`}
              x2="100%"
              y2={`${((i + 1) / rows) * 100}%`}
              stroke="rgba(0,0,0,0.3)"
              strokeWidth="1"
              strokeDasharray="5,5"
              strokeDashoffset="2.5"
            />
          ))}
        </svg>
      )}
    </div>
  );
}

GridPreview.propTypes = {
  imageData: PropTypes.string,
  rows: PropTypes.number.isRequired,
  columns: PropTypes.number.isRequired,
};

GridPreview.defaultProps = {
  imageData: null,
};

// Calculate recommended grid sizes based on image aspect ratio
function getGridRecommendations(aspectRatio) {
  if (!aspectRatio) return { recommended: [], notRecommended: [] };

  const allCombos = [];
  for (let r = 2; r <= 10; r += 1) {
    for (let c = 2; c <= 10; c += 1) {
      const gridRatio = c / r; // columns/rows = width/height
      const ratioDiff = Math.abs(gridRatio - aspectRatio) / aspectRatio;
      allCombos.push({
        rows: r, columns: c, ratioDiff, pieces: r * c,
      });
    }
  }

  // Recommended: within 10% of image aspect ratio (strict to prevent tiling)
  // Sort by piece count (ascending) so smaller puzzles come first
  const recommended = allCombos
    .filter((g) => g.ratioDiff < 0.1)
    .sort((a, b) => a.pieces - b.pieces);
  const notRecommended = allCombos
    .filter((g) => g.ratioDiff >= 0.1)
    .sort((a, b) => a.pieces - b.pieces);

  return { recommended, notRecommended };
}

const gridOptions = [
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
];

// 3 piece styles supported by PuzzleEngineV2
const pieceStyleOptions = [
  {
    value: 'traditional',
    label: 'Traditional',
    description: 'Bulbous knobs, narrow necks',
    isTraditional: true,
  },
  {
    value: 'rounded',
    label: 'Rounded',
    description: 'Soft circular tabs',
    isRounded: true,
  },
  {
    value: 'square',
    label: 'Square',
    description: 'Angular blocky tabs',
    isSquare: true,
  },
];

function PuzzleConfigForm({
  imageData,
  imageDimensions,
  rows,
  columns,
  pieceStyle,
  onRowsChange,
  onColumnsChange,
  onPieceStyleChange,
}) {
  // Get grid recommendations based on image aspect ratio
  const { recommended: recommendedGrids } = imageDimensions
    ? getGridRecommendations(imageDimensions.aspectRatio)
    : { recommended: [] };

  const pieceCount = rows * columns;

  return (
    <div className="space-y-6">
      {/* Grid Preview with overlay */}
      {imageData && (
        <div>
          <span className="block text-sm font-medium text-puzzle-text mb-2">
            Grid Preview
          </span>
          <GridPreview imageData={imageData} rows={rows} columns={columns} />
        </div>
      )}

      {/* Grid Size Selection */}
      <div>
        <span className="block text-sm font-medium text-puzzle-text mb-2">
          Grid Size
        </span>

        {/* Show recommendations if we have image dimensions */}
        {imageDimensions && recommendedGrids.length > 0 ? (
          <div className="space-y-4">
            {/* Recommended grids */}
            <div>
              <span className="text-sm text-green-600 font-medium flex items-center gap-1 mb-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Recommended for your image
              </span>
              <div className="grid grid-cols-4 gap-2">
                {recommendedGrids.map((g) => (
                  <button
                    key={`${g.rows}x${g.columns}`}
                    type="button"
                    onClick={() => { onRowsChange(g.rows); onColumnsChange(g.columns); }}
                    className={`p-2 rounded-lg border-2 text-center transition-colors ${
                      rows === g.rows && columns === g.columns
                        ? 'border-puzzle-blue bg-puzzle-blue/10'
                        : 'border-puzzle-border hover:border-puzzle-text-secondary'
                    }`}
                  >
                    <span className="block font-medium text-puzzle-text">
                      {g.rows}
                      {' x '}
                      {g.columns}
                    </span>
                    <span className="block text-xs text-puzzle-text-secondary">
                      {g.pieces}
                      {' pcs'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

          </div>
        ) : (
          /* Fallback: show dropdowns when no image dimensions available */
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="rows" className="block text-sm font-medium text-puzzle-text mb-2">
                Rows
                <select
                  id="rows"
                  value={rows}
                  onChange={(e) => onRowsChange(Number(e.target.value))}
                  className="mt-1 w-full px-4 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-puzzle-surface text-puzzle-text"
                >
                  {gridOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div>
              <label htmlFor="columns" className="block text-sm font-medium text-puzzle-text mb-2">
                Columns
                <select
                  id="columns"
                  value={columns}
                  onChange={(e) => onColumnsChange(Number(e.target.value))}
                  className="mt-1 w-full px-4 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-puzzle-surface text-puzzle-text"
                >
                  {gridOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </div>

      <div>
        <span className="block text-sm font-medium text-puzzle-text mb-2">
          Piece Style
        </span>
        <div className="grid grid-cols-2 gap-3">
          {pieceStyleOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onPieceStyleChange(option.value)}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                pieceStyle === option.value
                  ? 'border-puzzle-blue bg-puzzle-blue/10'
                  : 'border-puzzle-border hover:border-puzzle-text-secondary'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Render appropriate icon based on style type */}
                {option.isTraditional && (
                  <TraditionalPieceIcon className="w-8 h-8 text-puzzle-text-secondary" />
                )}
                {option.isRounded && (
                  <RoundedPieceIcon className="w-8 h-8 text-puzzle-text-secondary" />
                )}
                {option.isSquare && (
                  <SquarePieceIcon className="w-8 h-8 text-puzzle-text-secondary" />
                )}
                <div>
                  <span className="block font-medium text-puzzle-text">{option.label}</span>
                  <span className="block text-xs text-puzzle-text-secondary">{option.description}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-puzzle-muted rounded-lg p-4">
        <p className="text-sm text-puzzle-text-secondary">
          {'Puzzle size: '}
          <span className="font-semibold">
            {pieceCount}
            {' pieces'}
          </span>
          {` (${rows} x ${columns})`}
        </p>
      </div>
    </div>
  );
}

PuzzleConfigForm.propTypes = {
  imageData: PropTypes.string,
  imageDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    aspectRatio: PropTypes.number,
  }),
  rows: PropTypes.number.isRequired,
  columns: PropTypes.number.isRequired,
  pieceStyle: PropTypes.string.isRequired,
  onRowsChange: PropTypes.func.isRequired,
  onColumnsChange: PropTypes.func.isRequired,
  onPieceStyleChange: PropTypes.func.isRequired,
};

PuzzleConfigForm.defaultProps = {
  imageData: null,
  imageDimensions: null,
};

// Export helper function for use in parent components
export { getGridRecommendations };

export default PuzzleConfigForm;
